import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Card, Input, Loading, Modal, Select } from '../components/common';
import { Pagination } from '../components/common/Table';
import customerAccountService from '../services/customerAccountService';
import customerService from '../services/customerService';
import currencyService from '../services/currencyService';
import cashDrawerService from '../services/cashDrawerService';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';
import {
  WalletIcon,
  BanknotesIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  ArrowPathIcon,
  MagnifyingGlassIcon,
  TrashIcon,
  UserGroupIcon
} from '@heroicons/react/24/outline';

const CustomerAccountsPage = () => {
  const { t } = useTranslation();
  const { isAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  // Data
  const [summary, setSummary] = useState({ accounts: [], totals: [] });
  const [transactions, setTransactions] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [drawers, setDrawers] = useState([]);

  // Selected customer
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerAccounts, setCustomerAccounts] = useState([]);
  const [customerSearch, setCustomerSearch] = useState('');

  // View Mode
  const [viewMode, setViewMode] = useState('deposits'); // 'deposits' | 'loans'

  // Pagination for accounts table
  const [accountsPage, setAccountsPage] = useState(1);
  const [accountsLimit, setAccountsLimit] = useState(20);
  const [accountsTotalPages, setAccountsTotalPages] = useState(1);

  // Pagination for transactions table
  const [txnPage, setTxnPage] = useState(1);
  const [txnLimit, setTxnLimit] = useState(20);
  const [txnTotalPages, setTxnTotalPages] = useState(1);

  // Modals
  const [showDepositModal, setShowDepositModal] = useState(false);
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [showLoanModal, setShowLoanModal] = useState(false);
  const [showRepayModal, setShowRepayModal] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);

  // Form data
  const [formData, setFormData] = useState({
    currencyId: '',
    amount: '',
    customerName: '',
    customerPhone: '',
    method: 'cash',
    notes: '',
    drawerId: ''
  });

  const fetchData = useCallback(async (resetPages = false) => {
    setLoading(true);
    try {
      const currentAccountsPage = resetPages ? 1 : accountsPage;
      const currentTxnPage = resetPages ? 1 : txnPage;

      if (resetPages) {
        setAccountsPage(1);
        setTxnPage(1);
      }

      // Fetch concurrently but handle failures individually
      const [summaryRes, txnRes, currRes, drawerRes] = await Promise.all([
        customerAccountService.getAllAccounts({
          page: currentAccountsPage,
          limit: accountsLimit,
          type: viewMode
        }).catch(e => ({ success: false, error: e })),
        customerAccountService.getTransactions({
          page: currentTxnPage,
          limit: txnLimit,
          type: viewMode
        }).catch(e => ({ success: false, error: e })),
        currencyService.getCurrencies().catch(e => ({ success: false, error: e })),
        cashDrawerService.getDrawers({ active: true }).catch(e => ({ success: false, error: e }))
      ]);

      if (summaryRes.success) {
        setSummary(summaryRes.data);
        if (summaryRes.data.pagination) {
          setAccountsTotalPages(summaryRes.data.pagination.totalPages || 1);
        }
      }
      if (txnRes.success) {
        setTransactions(txnRes.data || []);
        if (txnRes.pagination) {
          setTxnTotalPages(txnRes.pagination.totalPages || 1);
        }
      }

      // Handle currency response - support multiple formats
      const currencyData = currRes.data || (Array.isArray(currRes) ? currRes : []);
      const currencyList = Array.isArray(currencyData) ? currencyData : [];
      setCurrencies(currencyList);

      // Handle drawer response
      const drawerData = drawerRes.data || (Array.isArray(drawerRes) ? drawerRes : []);
      const drawerList = Array.isArray(drawerData) ? drawerData : [];
      setDrawers(drawerList);

      // Set default currency and drawer in formData if not set
      setFormData(prev => ({
        ...prev,
        currencyId: prev.currencyId || (currencyList.length > 0 ? currencyList[0].id : ''),
        drawerId: prev.drawerId || (drawerList.length > 0 ? drawerList[0].uuid : '')
      }));
    } catch (error) {
      console.error("Fetch error", error);
      toast.error(t('common.error'));
    } finally {
      setLoading(false);
    }
  }, [t, accountsPage, accountsLimit, txnPage, txnLimit, viewMode]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Reset pagination when viewMode changes
  useEffect(() => {
    setAccountsPage(1);
    setTxnPage(1);
  }, [viewMode]);

  // Search customers
  const searchCustomers = async (search) => {
    setCustomerSearch(search);
    // Auto-update customer name for new customers
    if (!selectedCustomer) {
      setFormData(prev => ({ ...prev, customerName: search }));
    }

    if (!search || search.length < 2) {
      setCustomers([]);
      return;
    }
    try {
      const response = await customerService.searchCustomers(search);
      if (response.success) {
        setCustomers(response.data || []);
      }
    } catch (error) {
      // Silent error
    }
  };

  // Refs for scrolling
  const searchContainerRef = React.useRef(null);
  const selectedCustomerRef = React.useRef(null);

  // Select customer and load their accounts
  const selectCustomer = async (customer) => {
    setSelectedCustomer(customer);
    setCustomerSearch(customer.fullName);
    setFormData(prev => ({
      ...prev,
      customerName: customer.fullName,
      customerPhone: customer.phone
    }));
    setCustomers([]);

    try {
      const response = await customerAccountService.getCustomerAccounts(customer.uuid);
      if (response.success) {
        setCustomerAccounts(response.data.accounts || []);
      }
    } catch (error) {
      toast.error(t('common.error'));
    }

    // Scroll to the selected customer section after state updates
    setTimeout(() => {
      if (selectedCustomerRef.current) {
        selectedCustomerRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  const clearCustomer = () => {
    setSelectedCustomer(null);
    setCustomerAccounts([]);
    setCustomerSearch('');
    setFormData(prev => ({ ...prev, customerName: '', customerPhone: '' }));
  };

  // Modal openers
  const openDepositModal = () => {
    const defaultCurrency = currencies.length > 0 ? currencies[0].id : '';
    const defaultDrawer = drawers.length > 0 ? drawers[0].id : '';

    setFormData({
      currencyId: defaultCurrency,
      amount: '',
      customerName: selectedCustomer ? selectedCustomer.fullName : customerSearch,
      customerPhone: selectedCustomer ? selectedCustomer.phone : '',
      method: 'cash',
      notes: '',
      drawerId: defaultDrawer
    });
    setShowDepositModal(true);
  };

  const openWithdrawModal = (account) => {
    const defaultDrawer = drawers.length > 0 ? drawers[0].id : '';
    setSelectedAccount(account);
    setFormData({ amount: '', notes: '', drawerId: defaultDrawer });
    setShowWithdrawModal(true);
  };

  const openLoanModal = () => {
    const defaultCurrency = currencies.length > 0 ? currencies[0].id : '';
    const defaultDrawer = drawers.length > 0 ? drawers[0].id : '';

    setFormData({
      currencyId: defaultCurrency,
      amount: '',
      customerName: selectedCustomer ? selectedCustomer.fullName : customerSearch,
      customerPhone: selectedCustomer ? selectedCustomer.phone : '',
      method: 'cash',
      notes: '',
      drawerId: defaultDrawer
    });
    setShowLoanModal(true);
  };

  const openRepayModal = (account) => {
    const defaultDrawer = drawers.length > 0 ? drawers[0].id : '';
    setSelectedAccount(account);
    setFormData({ amount: '', notes: '', drawerId: defaultDrawer });
    setShowRepayModal(true);
  };

  // Handle deposit
  const handleDeposit = async (e) => {
    e.preventDefault();
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      toast.error(t('validation.positiveNumber'));
      return;
    }

    // Allow typing a name if no customer selected
    const nameToUse = selectedCustomer ? selectedCustomer.fullName : customerSearch;

    if (!nameToUse || !nameToUse.trim()) {
      toast.error(t('customers.nameRequired'));
      return;
    }

    if (formData.method === 'cash' && !formData.drawerId) {
      toast.error(t('validation.selectDrawer'));
      return;
    }

    try {
      setSubmitting(true);
      const payload = {
        currencyId: parseInt(formData.currencyId),
        amount: parseFloat(formData.amount),
        customerName: nameToUse.trim(),
        customerPhone: formData.customerPhone?.trim() || null,
        method: formData.method || 'cash',
        notes: formData.notes.trim() || null,
        drawerId: formData.method === 'cash' ? formData.drawerId : null
      };

      // Use existing customer UUID or 'new'
      const targetUuid = selectedCustomer ? selectedCustomer.uuid : 'new';

      const response = await customerAccountService.deposit(targetUuid, payload);

      if (response.success) {
        toast.success(t('depositsLoans.depositSuccess') || 'Deposit recorded');
        setShowDepositModal(false);
        fetchData();
        // If we selected a customer, refresh their view
        if (selectedCustomer) selectCustomer(selectedCustomer);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || t('common.error'));
    } finally {
      setSubmitting(false);
    }
  };

  // Handle withdrawal
  const handleWithdraw = async (e) => {
    e.preventDefault();
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      toast.error(t('validation.positiveNumber'));
      return;
    }

    if (!formData.drawerId) { // Withdrawals are cash operations
      toast.error(t('validation.selectDrawer', 'Please select a cash drawer'));
      return;
    }

    try {
      setSubmitting(true);
      const response = await customerAccountService.withdraw(selectedAccount.uuid, {
        amount: parseFloat(formData.amount),
        notes: formData.notes.trim() || null,
        method: 'cash',
        drawerId: formData.drawerId
      });

      if (response.success) {
        toast.success(t('depositsLoans.withdrawSuccess') || 'Withdrawal completed');
        setShowWithdrawModal(false);
        if (selectedCustomer) selectCustomer(selectedCustomer);
        fetchData();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || t('common.error'));
    } finally {
      setSubmitting(false);
    }
  };

  // Handle give loan
  const handleGiveLoan = async (e) => {
    e.preventDefault();
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      toast.error(t('validation.positiveNumber'));
      return;
    }

    const nameToUse = selectedCustomer ? selectedCustomer.fullName : customerSearch;
    if (!nameToUse || !nameToUse.trim()) {
      toast.error(t('customers.nameRequired'));
      return;
    }

    if (formData.method === 'cash' && !formData.drawerId) {
      toast.error(t('validation.selectDrawer'));
      return;
    }

    try {
      setSubmitting(true);
      const targetUuid = selectedCustomer ? selectedCustomer.uuid : 'new';

      const response = await customerAccountService.giveLoan(targetUuid, {
        currencyId: parseInt(formData.currencyId),
        amount: parseFloat(formData.amount),
        customerName: nameToUse.trim(),
        customerPhone: formData.customerPhone?.trim() || null,
        method: formData.method || 'cash',
        notes: formData.notes.trim() || null,
        drawerId: formData.method === 'cash' ? formData.drawerId : null
      });

      if (response.success) {
        toast.success(t('depositsLoans.loanGivenSuccess') || 'Loan recorded');
        setShowLoanModal(false);
        fetchData();
        if (selectedCustomer) selectCustomer(selectedCustomer);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || t('common.error'));
    } finally {
      setSubmitting(false);
    }
  };

  // Handle repay loan
  const handleRepayLoan = async (e) => {
    e.preventDefault();
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      toast.error(t('validation.positiveNumber'));
      return;
    }

    if (!formData.drawerId) {
      toast.error(t('validation.selectDrawer', 'Please select a cash drawer'));
      return;
    }

    try {
      setSubmitting(true);
      const response = await customerAccountService.repayLoan(selectedAccount.uuid, {
        amount: parseFloat(formData.amount),
        notes: formData.notes.trim() || null,
        method: 'cash',
        drawerId: formData.drawerId
      });

      if (response.success) {
        toast.success(response.message || t('depositsLoans.loanRepaidSuccess') || 'Loan repayment recorded');
        setShowRepayModal(false);
        if (selectedCustomer) selectCustomer(selectedCustomer);
        fetchData();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || t('common.error'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (uuid) => {
    if (!window.confirm(t('depositsLoans.confirmDeleteTransaction'))) {
      return;
    }

    try {
      setLoading(true);
      const response = await customerAccountService.deleteTransaction(uuid);
      if (response.success) {
        toast.success(response.message || t('transactions.transactionDeleted'));
        fetchData();
        if (selectedCustomer) selectCustomer(selectedCustomer);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount, symbol = '') => {
    if (amount === null || amount === undefined) return '0';
    const formatted = new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(amount);
    return symbol ? `${symbol}${formatted}` : formatted;
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString();
  };

  const getTypeColor = (type) => {
    const colors = {
      deposit: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
      withdrawal_cash: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
      loan_given: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400',
      loan_repayment: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400'
    };
    return colors[type] || 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400';
  };

  const getTypeLabel = (type) => {
    const labels = {
      deposit: t('depositsLoans.deposit') || 'Deposit',
      withdrawal_cash: t('depositsLoans.withdrawal') || 'Withdrawal',
      loan_given: t('depositsLoans.loanGiven') || 'Loan Given',
      loan_repayment: t('depositsLoans.loanRepayment') || 'Loan Repaid'
    };
    return labels[type] || type;
  };

  // Accounts are now filtered by backend based on viewMode
  const filteredAccounts = summary.accounts || [];

  // Handle accounts pagination
  const handleAccountsLimitChange = (e) => {
    setAccountsLimit(parseInt(e.target.value));
    setAccountsPage(1);
  };

  // Handle transactions pagination
  const handleTxnLimitChange = (e) => {
    setTxnLimit(parseInt(e.target.value));
    setTxnPage(1);
  };

  if (loading) {
    return <div className="flex justify-center py-12"><Loading size="lg" /></div>;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            {t('depositsLoans.title') || 'Deposits & Loans'}
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            {t('depositsLoans.subtitle') || 'Manage customer deposits and loans'}
          </p>
        </div>
        <div className="flex gap-2">
          <Button onClick={openDepositModal}>
            <ArrowDownIcon className="h-4 w-4 ltr:mr-1 rtl:ml-1" />
            {t('depositsLoans.deposit') || 'Deposit'}
          </Button>
          <Button variant="warning" onClick={openLoanModal}>
            <BanknotesIcon className="h-4 w-4 ltr:mr-1 rtl:ml-1" />
            {t('depositsLoans.giveLoan') || 'Give Loan'}
          </Button>
          <Button variant="secondary" onClick={() => fetchData(true)}>
            <ArrowPathIcon className="h-4 w-4 ltr:mr-1 rtl:ml-1" />
            {t('common.refresh')}
          </Button>
        </div>
      </div>

      {/* Global Filter Tabs */}
      <div className="flex justify-center sm:justify-start">
        <div className="flex bg-gray-100 dark:bg-gray-700 rounded-lg p-1 w-full sm:w-auto">
          <button
            onClick={() => setViewMode('deposits')}
            className={`flex-1 sm:flex-none flex items-center justify-center px-8 py-2 text-sm font-bold rounded-md transition-all ${viewMode === 'deposits'
              ? 'bg-white dark:bg-gray-600 text-green-600 dark:text-green-400 shadow-sm'
              : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
          >
            <BanknotesIcon className="h-5 w-5 mr-2" />
            {t('depositsLoans.deposits') || 'Deposits'}
          </button>
          <button
            onClick={() => setViewMode('loans')}
            className={`flex-1 sm:flex-none flex items-center justify-center px-8 py-2 text-sm font-bold rounded-md transition-all ${viewMode === 'loans'
              ? 'bg-white dark:bg-gray-600 text-orange-600 dark:text-orange-400 shadow-sm'
              : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
          >
            <WalletIcon className="h-5 w-5 mr-2" />
            {t('depositsLoans.loans') || 'Loans'}
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {summary.totals.map((total) => (
          <Card key={total.currencyCode} className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400 font-bold">{total.currencyCode}</p>
                {viewMode === 'deposits' ? (
                  <p className="text-xl font-bold text-green-600 dark:text-green-400">
                    {formatCurrency(total.totalDeposits, total.currencySymbol)}
                  </p>
                ) : (
                  <p className="text-xl font-bold text-orange-600 dark:text-orange-400">
                    {formatCurrency(total.totalLoans, total.currencySymbol)}
                  </p>
                )}
                <p className="text-xs text-gray-400 mt-1 uppercase tracking-wider">
                  {viewMode === 'deposits' ? t('depositsLoans.deposits') : t('depositsLoans.loans')}
                </p>
              </div>
              {viewMode === 'deposits' ? (
                <BanknotesIcon className="h-10 w-10 text-green-100 dark:text-green-900/30" />
              ) : (
                <WalletIcon className="h-10 w-10 text-orange-100 dark:text-orange-900/30" />
              )}
            </div>
          </Card>
        ))}
        {summary.totals.length === 0 && (
          <Card className="col-span-full p-6 text-center text-gray-500 dark:text-gray-400">
            {t('depositsLoans.noAccounts') || 'No customer accounts yet'}
          </Card>
        )}
      </div>

      {/* Active Accounts Table Section */}
      <Card>
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
            <UserGroupIcon className="h-5 w-5 mr-2" />
            {viewMode === 'deposits' ? t('depositsLoans.activeDeposits') : t('depositsLoans.activeLoans')}
          </h2>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-800">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider rtl:text-right">
                  {t('common.name')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider rtl:text-right">
                  {t('common.phone')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider rtl:text-right">
                  {t('common.currency')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider rtl:text-right">
                  {viewMode === 'deposits' ? t('depositsLoans.depositBalance') : t('depositsLoans.loanBalance')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider rtl:text-right">
                  {t('common.actions')}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
              {filteredAccounts.length > 0 ? (
                filteredAccounts.map((account) => (
                  <tr
                    key={`${account.customerUuid}-${account.currencyCode}`}
                    className="hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer"
                    onClick={() => selectCustomer({
                      uuid: account.customerUuid,
                      fullName: account.customerName,
                      phone: account.customerPhone
                    })}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        {account.customerName}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {account.customerPhone || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {account.currencyCode}
                      </span>
                    </td>
                    <td className={`px-6 py-4 whitespace-nowrap text-sm font-bold ${viewMode === 'deposits' ? 'text-green-600' : 'text-orange-600'
                      }`}>
                      {viewMode === 'deposits'
                        ? formatCurrency(account.balance, account.currencySymbol)
                        : formatCurrency(account.loanBalance, account.currencySymbol)
                      }
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <Button size="sm" variant="secondary" onClick={(e) => {
                        e.stopPropagation();
                        selectCustomer({
                          uuid: account.customerUuid,
                          fullName: account.customerName,
                          phone: account.customerPhone
                        });
                      }}>
                        {t('common.view')}
                      </Button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" className="px-6 py-8 text-center text-gray-500 dark:text-gray-400">
                    {t('common.noData') || 'No data available'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Accounts Pagination */}
        {accountsTotalPages > 1 && (
          <div className="border-t border-gray-200 dark:border-gray-700 p-4 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-500 dark:text-gray-400">{t('common.rowsPerPage')}:</span>
              <select
                value={accountsLimit}
                onChange={handleAccountsLimitChange}
                className="border-gray-300 dark:border-gray-600 rounded-md text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="20">20</option>
                <option value="50">50</option>
                <option value="100">100</option>
                <option value="500">500</option>
              </select>
            </div>
            <Pagination
              page={accountsPage}
              totalPages={accountsTotalPages}
              onPageChange={setAccountsPage}
            />
          </div>
        )}
      </Card>

      {/* Customer Selection */}
      <Card className="p-4" ref={searchContainerRef}>
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <MagnifyingGlassIcon className="absolute ltr:left-3 rtl:right-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder={t('depositsLoans.searchCustomer') || 'Search customer by name or phone...'}
              value={customerSearch}
              onChange={(e) => {
                setCustomerSearch(e.target.value);
                searchCustomers(e.target.value);
              }}
              className="w-full ltr:pl-10 rtl:pr-10 ltr:pr-3 rtl:pl-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
            {/* Search results dropdown */}
            {customers.length > 0 && (
              <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg max-h-60 overflow-auto">
                {customers.map((customer) => (
                  <button
                    key={customer.uuid}
                    onClick={() => selectCustomer(customer)}
                    className="w-full px-4 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    <div className="font-medium">{customer.fullName}</div>
                    <div className="text-sm text-gray-500">{customer.phone}</div>
                  </button>
                ))}
              </div>
            )}
          </div>
          {selectedCustomer && (
            <Button variant="secondary" onClick={clearCustomer}>
              {t('common.clear') || 'Clear'}
            </Button>
          )}
        </div>
      </Card>

      {/* Selected Customer Accounts */}
      {selectedCustomer && (
        <div className="space-y-4" ref={selectedCustomerRef}>
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              {selectedCustomer.fullName}
            </h2>
            <div className="flex gap-2">
              <Button onClick={openDepositModal}>
                <ArrowDownIcon className="h-4 w-4 ltr:mr-1 rtl:ml-1" />
                {t('depositsLoans.deposit') || 'Deposit'}
              </Button>
              <Button variant="warning" onClick={openLoanModal}>
                <ArrowUpIcon className="h-4 w-4 ltr:mr-1 rtl:ml-1" />
                {t('depositsLoans.giveLoan') || 'Give Loan'}
              </Button>
            </div>
          </div>

          {/* Account Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {customerAccounts
              .filter(acc => viewMode === 'deposits' ? acc.balance > 0 : acc.loanBalance > 0)
              .map((account) => (
                <Card key={account.uuid} className="p-4">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center">
                      <div className={`h-10 w-10 rounded-full flex items-center justify-center ${viewMode === 'deposits' ? 'bg-green-100 text-green-600' : 'bg-orange-100 text-orange-600'}`}>
                        {viewMode === 'deposits' ? <BanknotesIcon className="h-5 w-5" /> : <WalletIcon className="h-5 w-5" />}
                      </div>
                      <div className="ltr:ml-3 rtl:mr-3">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                          {account.currencyCode}
                        </h3>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2 mb-4">
                    {viewMode === 'deposits' ? (
                      <div className="flex justify-between">
                        <span className="text-gray-500 dark:text-gray-400">{t('depositsLoans.depositBalance')}:</span>
                        <span className="font-bold text-xl text-green-600 dark:text-green-400">
                          {formatCurrency(account.balance, account.currencySymbol)}
                        </span>
                      </div>
                    ) : (
                      <div className="flex justify-between">
                        <span className="text-gray-500 dark:text-gray-400">{t('depositsLoans.loanBalance')}:</span>
                        <span className="font-bold text-xl text-orange-600 dark:text-orange-400">
                          {formatCurrency(account.loanBalance, account.currencySymbol)}
                        </span>
                      </div>
                    )}
                  </div>

                  <div className="flex flex-wrap gap-2">
                    {viewMode === 'deposits' && account.balance > 0 && (
                      <Button size="sm" variant="secondary" onClick={() => openWithdrawModal(account)}>
                        <ArrowUpIcon className="h-3 w-3 ltr:mr-1 rtl:ml-1" />
                        {t('depositsLoans.withdraw') || 'Withdraw'}
                      </Button>
                    )}
                    {viewMode === 'loans' && account.loanBalance > 0 && (
                      <Button size="sm" variant="secondary" onClick={() => openRepayModal(account)}>
                        {t('depositsLoans.repayLoan') || 'Repay'}
                      </Button>
                    )}
                  </div>
                </Card>
              ))}
            {(viewMode === 'deposits' ?
              customerAccounts.filter(a => a.balance > 0).length === 0 :
              customerAccounts.filter(a => a.loanBalance > 0).length === 0) && (
                <Card className="col-span-full p-6 text-center text-gray-500 dark:text-gray-400">
                  {t('depositsLoans.noAccountsForCustomer') || 'No active accounts for selected mode.'}
                </Card>
              )}
          </div>
        </div>
      )}

      {/* Recent Transactions */}
      <Card>
        <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            {viewMode === 'deposits' ? t('depositsLoans.depositTransactions') || 'Deposit Transactions' : t('depositsLoans.loanTransactions') || 'Loan Transactions'}
          </h3>
          <span className="text-xs font-medium text-gray-400 uppercase tracking-wider">
            {viewMode === 'deposits' ? t('depositsLoans.deposits') : t('depositsLoans.loans')}
          </span>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-800">
              <tr>
                <th className="table-header">{t('common.date')}</th>
                <th className="table-header">{t('customers.title')}</th>
                <th className="table-header">{t('common.type')}</th>
                <th className="table-header">{t('currencies.currency')}</th>
                <th className="table-header">{t('common.amount')}</th>
                <th className="table-header">{t('depositsLoans.balanceAfter') || 'Balance'}</th>
                <th className="table-header">{t('common.notes')}</th>
                {isAdmin() && <th className="table-header">{t('common.actions')}</th>}
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
              {transactions.length > 0 ? (
                transactions.map((txn) => (
                  <tr key={txn.uuid} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                    <td className="table-cell text-sm">{formatDateTime(txn.createdAt)}</td>
                    <td className="table-cell font-medium">{txn.customerName}</td>
                    <td className="table-cell">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getTypeColor(txn.type)}`}>
                        {getTypeLabel(txn.type)}
                      </span>
                    </td>
                    <td className="table-cell">{txn.currencyCode}</td>
                    <td className="table-cell font-medium">
                      <span className={txn.type === 'withdrawal_cash' || txn.type === 'loan_given' ? 'text-red-600' : 'text-green-600'}>
                        {txn.type === 'withdrawal_cash' || txn.type === 'loan_given' ? '-' : '+'}
                        {formatCurrency(txn.amount, txn.currencySymbol)}
                      </span>
                    </td>
                    <td className="table-cell">
                      {viewMode === 'deposits' ?
                        formatCurrency(txn.balanceAfter, txn.currencySymbol) :
                        formatCurrency(txn.loanBalanceAfter, txn.currencySymbol)
                      }
                    </td>
                    <td className="table-cell text-gray-500 text-sm">{txn.notes || '-'}</td>
                    {isAdmin() && (
                      <td className="table-cell">
                        <button
                          onClick={() => handleDelete(txn.uuid)}
                          className="p-1 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                          title={t('common.delete')}
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </td>
                    )}
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={isAdmin() ? 8 : 7} className="text-center py-8 text-gray-500 dark:text-gray-400">
                    {t('common.noData')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Transactions Pagination */}
        {txnTotalPages > 1 && (
          <div className="border-t border-gray-200 dark:border-gray-700 p-4 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-500 dark:text-gray-400">{t('common.rowsPerPage')}:</span>
              <select
                value={txnLimit}
                onChange={handleTxnLimitChange}
                className="border-gray-300 dark:border-gray-600 rounded-md text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="20">20</option>
                <option value="50">50</option>
                <option value="100">100</option>
                <option value="500">500</option>
              </select>
            </div>
            <Pagination
              page={txnPage}
              totalPages={txnTotalPages}
              onPageChange={setTxnPage}
            />
          </div>
        )}
      </Card>

      {/* Deposit Modal */}
      <Modal
        isOpen={showDepositModal}
        onClose={() => setShowDepositModal(false)}
        title={t('depositsLoans.deposit') || 'Deposit'}
      >
        <form onSubmit={handleDeposit} className="space-y-4">
          {!selectedCustomer && (
            <div className="relative">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('customers.customer') || 'Customer'}
              </label>
              <input
                type="text"
                placeholder={t('depositsLoans.searchCustomer') || 'Search customer...'}
                value={customerSearch}
                onChange={(e) => {
                  setCustomerSearch(e.target.value);
                  searchCustomers(e.target.value);
                }}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                required
              />
              {customers.length > 0 && (
                <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg max-h-60 overflow-auto">
                  {customers.map((customer) => (
                    <button
                      key={customer.uuid}
                      type="button"
                      onClick={() => selectCustomer(customer)}
                      className="w-full px-4 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-900 dark:text-white"
                    >
                      <div className="font-medium">{customer.fullName}</div>
                      <div className="text-sm text-gray-500">{customer.phone}</div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          {selectedCustomer && (
            <div className="bg-blue-50 dark:bg-blue-900/30 p-3 rounded-md">
              <p className="text-sm font-medium text-gray-900 dark:text-white">
                {t('customers.customer') || 'Customer'}: {selectedCustomer.fullName}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">{selectedCustomer.phone}</p>
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Select
              label={t('currencies.currency')}
              value={formData.currencyId}
              onChange={(e) => setFormData({ ...formData, currencyId: e.target.value })}
              options={currencies.map(c => ({ value: c.id, label: `${c.code} - ${c.name}` }))}
              placeholder={t('currencies.selectCurrency') || 'Select Currency'}
              required
            />
            <Select
              label={t('common.cashDrawer')}
              value={formData.drawerId}
              onChange={(e) => setFormData({ ...formData, drawerId: e.target.value })}
              options={drawers.map(d => ({ value: d.id, label: d.name }))}
              placeholder={t('common.selectDrawer') || 'Select Drawer'}
              required
            />
          </div>
          <Input
            label={t('common.amount')}
            type="number"
            step="0.01"
            min="0.01"
            value={formData.amount}
            onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
            required
          />
          <Input
            label={t('common.notes')}
            value={formData.notes}
            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
          />
          <div className="flex justify-end gap-3 pt-4">
            <Button variant="secondary" type="button" onClick={() => setShowDepositModal(false)}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? t('common.loading') : t('depositsLoans.deposit')}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Withdraw Modal */}
      <Modal
        isOpen={showWithdrawModal}
        onClose={() => setShowWithdrawModal(false)}
        title={t('depositsLoans.withdraw')}
      >
        <form onSubmit={handleWithdraw} className="space-y-4">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {t('depositsLoans.availableBalance')}: {selectedAccount && formatCurrency(selectedAccount.balance, selectedAccount.currencySymbol)}
          </p>
          <Select
            label={t('common.cashDrawer')}
            value={formData.drawerId}
            onChange={(e) => setFormData({ ...formData, drawerId: e.target.value })}
            options={drawers.map(d => ({ value: d.id, label: d.name }))}
            placeholder={t('common.selectDrawer') || 'Select Drawer'}
            required
          />
          <Input
            label={t('common.amount')}
            type="number"
            step="0.01"
            min="0.01"
            max={selectedAccount?.balance}
            value={formData.amount}
            onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
            required
          />
          <Input
            label={t('common.notes')}
            value={formData.notes}
            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
          />
          <div className="flex justify-end gap-3 pt-4">
            <Button variant="secondary" type="button" onClick={() => setShowWithdrawModal(false)}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? t('common.loading') : t('depositsLoans.withdraw')}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Give Loan Modal */}
      <Modal
        isOpen={showLoanModal}
        onClose={() => setShowLoanModal(false)}
        title={t('depositsLoans.giveLoan')}
      >
        <form onSubmit={handleGiveLoan} className="space-y-4">
          {!selectedCustomer && (
            <div className="relative">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('customers.customer')}
              </label>
              <input
                type="text"
                placeholder={t('depositsLoans.searchCustomer')}
                value={customerSearch}
                onChange={(e) => {
                  setCustomerSearch(e.target.value);
                  searchCustomers(e.target.value);
                }}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                required
              />
              {customers.length > 0 && (
                <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg max-h-60 overflow-auto">
                  {customers.map((customer) => (
                    <button
                      key={customer.uuid}
                      type="button"
                      onClick={() => selectCustomer(customer)}
                      className="w-full px-4 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-900 dark:text-white"
                    >
                      <div className="font-medium">{customer.fullName}</div>
                      <div className="text-sm text-gray-500">{customer.phone}</div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          {selectedCustomer && (
            <div className="bg-orange-50 dark:bg-orange-900/30 p-3 rounded-md">
              <p className="text-sm font-medium text-gray-900 dark:text-white">
                {t('customers.customer')}: {selectedCustomer.fullName}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">{selectedCustomer.phone}</p>
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Select
              label={t('currencies.currency')}
              value={formData.currencyId}
              onChange={(e) => setFormData({ ...formData, currencyId: e.target.value })}
              options={currencies.map(c => ({ value: c.id, label: `${c.code} - ${c.name}` }))}
              placeholder={t('currencies.selectCurrency') || 'Select Currency'}
              required
            />
            <Select
              label={t('common.cashDrawer')}
              value={formData.drawerId}
              onChange={(e) => setFormData({ ...formData, drawerId: e.target.value })}
              options={drawers.map(d => ({ value: d.id, label: d.name }))}
              placeholder={t('common.selectDrawer') || 'Select Drawer'}
              required
            />
          </div>
          <Input
            label={t('common.amount')}
            type="number"
            step="0.01"
            min="0.01"
            value={formData.amount}
            onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
            required
          />
          <Input
            label={t('common.notes')}
            value={formData.notes}
            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
          />
          <div className="flex justify-end gap-3 pt-4">
            <Button variant="secondary" type="button" onClick={() => setShowLoanModal(false)}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" variant="warning" disabled={submitting}>
              {submitting ? t('common.loading') : t('depositsLoans.giveLoan')}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Repay Loan Modal */}
      <Modal
        isOpen={showRepayModal}
        onClose={() => setShowRepayModal(false)}
        title={t('depositsLoans.repayLoan')}
      >
        <form onSubmit={handleRepayLoan} className="space-y-4">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {t('depositsLoans.loanBalance')}: {selectedAccount && formatCurrency(selectedAccount.loanBalance, selectedAccount.currencySymbol)}
          </p>
          <Select
            label={t('common.cashDrawer')}
            value={formData.drawerId}
            onChange={(e) => setFormData({ ...formData, drawerId: e.target.value })}
            options={drawers.map(d => ({ value: d.id, label: d.name }))}
            placeholder={t('common.selectDrawer') || 'Select Drawer'}
            required
          />
          <Input
            label={t('common.amount')}
            type="number"
            step="0.01"
            min="0.01"
            value={formData.amount}
            onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
            required
          />
          <p className="text-xs text-gray-400">
            {t('depositsLoans.overpaymentNote')}
          </p>
          <Input
            label={t('common.notes')}
            value={formData.notes}
            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
          />
          <div className="flex justify-end gap-3 pt-4">
            <Button variant="secondary" type="button" onClick={() => setShowRepayModal(false)}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? t('common.loading') : t('depositsLoans.repayLoan')}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default CustomerAccountsPage;
